<template>
  <div class="canvas-showcase">
    <div class="header-overlay">
      <div class="title-box">
        <span class="prefix">NODE_LABS /</span>
        <h1>来秋社区技术实验室</h1>
        <div class="status-bar">
          <span class="dot"></span> SYSTEM_STABLE_ACTIVE
        </div>
      </div>
    </div>

    <div class="data-stream-left">
      <div v-for="i in 10" :key="i" class="code-line">
        {{ generateHex() }}
      </div>
    </div>

    <canvas ref="canvasRef" class="main-canvas"></canvas>

    <div class="footer-overlay">
      <div class="stats-group">
        <div class="stat-item">
          <span class="label">CORES</span>
          <span class="val">0xFA22</span>
        </div>
        <div class="stat-item">
          <span class="label">LATENCY</span>
          <span class="val">1.2ms</span>
        </div>
        <div class="stat-item">
          <span class="label">STABILITY</span>
          <span class="val">99.99%</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const canvasRef = ref(null)
let ctx = null
let animationId = null
let particles = []
const mouse = { x: null, y: null, radius: 150 }

// 生成模拟十六进制代码
const generateHex = () => Math.random().toString(16).substring(2, 10).toUpperCase()

// 粒子类定义
class Particle {
  constructor(w, h) {
    this.x = Math.random() * w
    this.y = Math.random() * h
    this.baseX = this.x
    this.baseY = this.y
    this.size = Math.random() * 2 + 1
    this.density = (Math.random() * 30) + 1
    this.color = '#409eff'
  }

  draw() {
    ctx.fillStyle = this.color
    ctx.beginPath()
    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2)
    ctx.closePath()
    ctx.fill()
  }

  update() {
    // 鼠标交互逻辑
    let dx = mouse.x - this.x
    let dy = mouse.y - this.y
    let distance = Math.sqrt(dx * dx + dy * dy)
    let forceDirectionX = dx / distance
    let forceDirectionY = dy / distance
    let maxDistance = mouse.radius
    let force = (maxDistance - distance) / maxDistance
    let directionX = forceDirectionX * force * this.density
    let directionY = forceDirectionY * force * this.density

    if (distance < mouse.radius) {
      this.x -= directionX
      this.y -= directionY
    } else {
      if (this.x !== this.baseX) {
        let dx = this.x - this.baseX
        this.x -= dx / 10
      }
      if (this.y !== this.baseY) {
        let dy = this.y - this.baseY
        this.y -= dy / 10
      }
    }
  }
}

const initCanvas = () => {
  const canvas = canvasRef.value
  ctx = canvas.getContext('2d')
  canvas.width = window.innerWidth
  canvas.height = window.innerHeight

  particles = []
  // 根据屏幕大小生成粒子
  const numberOfParticles = (canvas.width * canvas.height) / 8000
  for (let i = 0; i < numberOfParticles; i++) {
    particles.push(new Particle(canvas.width, canvas.height))
  }
}

const connect = () => {
  for (let a = 0; a < particles.length; a++) {
    for (let b = a; b < particles.length; b++) {
      let dx = particles[a].x - particles[b].x
      let dy = particles[a].y - particles[b].y
      let distance = Math.sqrt(dx * dx + dy * dy)

      if (distance < 100) {
        ctx.strokeStyle = `rgba(64, 158, 255, ${1 - distance / 100})`
        ctx.lineWidth = 0.5
        ctx.beginPath()
        ctx.moveTo(particles[a].x, particles[a].y)
        ctx.lineTo(particles[b].x, particles[b].y)
        ctx.stroke()
      }
    }
  }
}

const animate = () => {
  ctx.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height)
  for (let i = 0; i < particles.length; i++) {
    particles[i].update()
    particles[i].draw()
  }
  connect()
  animationId = requestAnimationFrame(animate)
}

onMounted(() => {
  initCanvas()
  animate()

  window.addEventListener('mousemove', (e) => {
    mouse.x = e.x
    mouse.y = e.y
  })

  window.addEventListener('resize', initCanvas)
})

onUnmounted(() => {
  cancelAnimationFrame(animationId)
  window.removeEventListener('mousemove', () => { })
  window.removeEventListener('resize', initCanvas)
})
</script>

<style scoped lang="scss">
.canvas-showcase {
  position: relative;
  width: 100%;
  height: 100vh;
  background: #000;
  overflow: hidden;
  color: #fff;
  font-family: 'JetBrains Mono', monospace;
}

.main-canvas {
  position: absolute;
  top: 0;
  left: 0;
}

/* 顶部覆盖层 */
.header-overlay {
  position: absolute;
  top: 40px;
  left: 40px;
  z-index: 10;
  pointer-events: none;

  .prefix {
    color: #409eff;
    font-size: 12px;
    letter-spacing: 2px;
  }

  h1 {
    font-size: 24px;
    margin: 8px 0;
    font-weight: 200;
    letter-spacing: 4px;
  }

  .status-bar {
    font-size: 10px;
    display: flex;
    align-items: center;
    gap: 8px;
    color: rgba(255, 255, 255, 0.4);

    .dot {
      width: 6px;
      height: 6px;
      background: #00ff88;
      border-radius: 50%;
      box-shadow: 0 0 10px #00ff88;
      animation: blink 1.5s infinite;
    }
  }
}

/* 数据流装饰 */
.data-stream-left {
  position: absolute;
  left: 40px;
  top: 150px;
  z-index: 5;
  opacity: 0.2;
  font-size: 10px;
  line-height: 2;
  pointer-events: none;
}

/* 底部状态 */
.footer-overlay {
  position: absolute;
  bottom: 40px;
  right: 40px;
  z-index: 10;

  .stats-group {
    display: flex;
    gap: 40px;
    background: rgba(255, 255, 255, 0.05);
    padding: 15px 30px;
    border-radius: 4px;
    border-right: 2px solid #409eff;
    backdrop-filter: blur(10px);
  }

  .stat-item {
    display: flex;
    flex-direction: column;

    .label {
      font-size: 9px;
      color: #666;
    }

    .val {
      font-size: 14px;
      font-weight: bold;
      color: #409eff;
    }
  }
}

@keyframes blink {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.3;
  }
}
</style>